import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { getModuleSetupProfile, getModuleSetupProfiles } from '../api/setupProfilesApi';
import { colorTokens, radiusTokens, typographyTokens } from '../app/designTokens';
import { DataTable } from '../components/DataTable';
import { DetailField } from '../components/InfoCard';
import { LoadingSkeleton } from '../components/LoadingSkeleton';
import { PageHeader } from '../components/PageHeader';
import { SectionCard } from '../components/SectionCard';
import { StatusBadge } from '../components/StatusBadge';
import { TableToolbar } from '../components/TableToolbar';
import { Button } from '../components/ui/Button';
import { IconButton } from '../components/ui/IconButton';

const { semantic } = colorTokens;

const CATEGORY_TONE = {
  global_mandatory: 'Brand',
  module_required: 'Success',
  support: 'Info',
  future: 'Warning',
};

const detailGridSx = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
  gap: 2.5,
  minWidth: 0,
  maxWidth: '100%',
  '& > *': { minWidth: 0, maxWidth: '100%' },
};

const monoSx = {
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
  fontSize: 12,
  lineHeight: '18px',
  color: semantic.text.primary,
  minWidth: 0,
  maxWidth: '100%',
  overflowWrap: 'anywhere',
  wordBreak: 'break-word',
};

const wrapSx = {
  fontSize: 13,
  lineHeight: '18px',
  color: semantic.text.primary,
  overflowWrap: 'anywhere',
  wordBreak: 'break-word',
};

function YesNoBadge({ value, t }) {
  return (
    <StatusBadge
      status={value ? 'yes' : 'no'}
      category="generic"
      apiLabel={value ? t('common:yes') : t('common:no')}
      size="Sm"
      tone={value ? 'Success' : 'Neutral'}
    />
  );
}

function categoryLabel(t, category) {
  if (!category) return null;
  return t(`setupProfiles:categories.${category}`, {
    defaultValue: t(`setupProfiles:seedScopes.${category}`, { defaultValue: category }),
  });
}

function seedScopeLabel(t, scope) {
  if (!scope) return null;
  return t(`setupProfiles:seedScopes.${scope}`, { defaultValue: scope });
}

function TechList({ label, values }) {
  if (!values?.length) return null;
  return (
    <DetailField label={label}>
      <Typography sx={monoSx}>{values.join(', ')}</Typography>
    </DetailField>
  );
}

export function ModuleSetupProfilesPage() {
  const { t } = useTranslation(['setupProfiles', 'common']);
  const [searchParams] = useSearchParams();
  const [profiles, setProfiles] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const moduleFilter = searchParams.get('module_code') || '';

  const emptyValue = t('common:empty_value');
  const recordCount = profiles.length;

  const loadProfiles = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params = {};
      if (moduleFilter) {
        params.module_code = moduleFilter;
      }
      const response = await getModuleSetupProfiles(params);
      setProfiles(response.data || []);
      setMeta(response.meta || null);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [moduleFilter]);

  useEffect(() => {
    loadProfiles();
  }, [loadProfiles]);

  const openDetail = useCallback(async (profileId) => {
    setDetailLoading(true);
    setDetail(null);
    try {
      const data = await getModuleSetupProfile(profileId);
      setDetail(data);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const closeDetail = () => {
    setDetail(null);
    setDetailLoading(false);
  };

  const itemColumns = useMemo(
    () => [
      {
        id: 'title',
        label: t('setupProfiles:profileItems'),
        render: (row) => row.title || emptyValue,
      },
      {
        id: 'item_type',
        label: t('common:type'),
        render: (row) => (
          <Typography sx={monoSx}>{row.item_type || emptyValue}</Typography>
        ),
      },
      {
        id: 'is_required',
        label: t('setupProfiles:globalRequired'),
        render: (row) => <YesNoBadge value={Boolean(row.is_required)} t={t} />,
      },
    ],
    [emptyValue, t],
  );

  const columns = useMemo(
    () => [
      {
        id: 'module_customer_name',
        label: t('common:module'),
        render: (row) => row.module_customer_name || row.module_code || emptyValue,
      },
      {
        id: 'title',
        label: t('setupProfiles:profile'),
        render: (row) => row.title || emptyValue,
      },
      {
        id: 'setup_category',
        label: t('common:category'),
        render: (row) => (
          row.setup_category
            ? (
              <StatusBadge
                status={row.setup_category}
                category="generic"
                apiLabel={categoryLabel(t, row.setup_category)}
                size="Sm"
                tone={CATEGORY_TONE[row.setup_category] || 'Neutral'}
              />
            )
            : emptyValue
        ),
      },
      {
        id: 'is_required',
        label: t('setupProfiles:globalRequired'),
        render: (row) => <YesNoBadge value={Boolean(row.is_required)} t={t} />,
      },
      {
        id: 'is_always_seeded',
        label: t('setupProfiles:alwaysSeeded'),
        render: (row) => <YesNoBadge value={Boolean(row.is_always_seeded)} t={t} />,
      },
      {
        id: 'seed_scope',
        label: t('setupProfiles:seedScope'),
        render: (row) => (
          row.seed_scope
            ? <Typography sx={monoSx}>{seedScopeLabel(t, row.seed_scope)}</Typography>
            : emptyValue
        ),
      },
      {
        id: 'required_before_use',
        label: t('setupProfiles:requiredBeforeUse'),
        render: (row) => <YesNoBadge value={Boolean(row.required_before_use)} t={t} />,
      },
      {
        id: 'wizard_step_key',
        label: t('setupProfiles:wizardStep'),
        render: (row) => (
          row.wizard_step_key
            ? <Typography sx={monoSx}>{row.wizard_step_key}</Typography>
            : emptyValue
        ),
      },
      {
        id: 'items_count',
        label: t('setupProfiles:profileItems'),
        render: (row) => (
          row.items_count === 0 || row.items_count ? row.items_count : emptyValue
        ),
      },
      {
        id: 'actions',
        label: t('common:actions'),
        align: 'right',
        width: 72,
        render: (row) => (
          <IconButton
            size="Sm"
            tone="Neutral"
            aria-label={t('common:view_detail')}
            onClick={(event) => {
              event.stopPropagation();
              openDetail(row.id);
            }}
          >
            <ChevronRightIcon />
          </IconButton>
        ),
      },
    ],
    [emptyValue, openDetail, t],
  );

  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      <PageHeader
        type="List"
        title={t('setupProfiles:title')}
        subtitle={t('setupProfiles:description')}
        actions={(
          <Button variant="Secondary" component={RouterLink} to="/setup-wizard">
            {t('setupProfiles:openSetupWizard')}
          </Button>
        )}
      />

      <Alert severity="info" sx={{ mb: 2 }}>
        {t('setupProfiles:blueprintNotice')}
      </Alert>
      {moduleFilter && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('setupProfiles:filteredByModule', { module: moduleFilter })}
        </Alert>
      )}
      {meta?.profiles_count != null && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {t('setupProfiles:summary', { count: meta.profiles_count })}
        </Alert>
      )}

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={(
            <Button variant="Ghost" onClick={loadProfiles} sx={{ minHeight: 32 }}>
              {t('common:retry')}
            </Button>
          )}
        >
          {t('setupProfiles:load_failed')}
        </Alert>
      )}

      <SectionCard variant="Default" noPadding sx={{ width: '100%' }}>
        <TableToolbar
          mode="Default"
          title={t('setupProfiles:title')}
          meta={(
            <StatusBadge
              status="list"
              category="generic"
              apiLabel={t('setupProfiles:toolbar_count', { count: recordCount })}
              size="Sm"
              tone="Neutral"
            />
          )}
        />

        {loading ? (
          <LoadingSkeleton
            type="Table"
            embedded
            rows={3}
            columns={columns.length}
          />
        ) : (
          <DataTable
            type="Default"
            embedded
            columns={columns}
            rows={profiles}
            getRowKey={(row) => String(row.id)}
            emptyMessage={t('setupProfiles:no_profiles')}
            onRowClick={(row) => openDetail(row.id)}
            sx={{
              overflowX: 'auto',
              width: '100%',
              '& table': {
                width: '100%',
                minWidth: '100%',
              },
            }}
          />
        )}
      </SectionCard>

      <Dialog
        open={Boolean(detail) || detailLoading}
        onClose={closeDetail}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: 'min(860px, calc(100vw - 48px))',
            maxWidth: 'min(860px, calc(100vw - 48px))',
            maxHeight: 'calc(100vh - 64px)',
            bgcolor: semantic.bg.surface,
            backgroundImage: 'none',
            border: `1px solid ${semantic.border.subtle}`,
            borderRadius: `${radiusTokens.card}px`,
          },
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: typographyTokens.fontFamily,
            fontWeight: typographyTokens.fontWeightSemibold,
            fontSize: 16,
            color: semantic.text.primary,
            borderBottom: `1px solid ${semantic.border.subtle}`,
          }}
        >
          {detail?.title || t('setupProfiles:profileDetail')}
        </DialogTitle>
        <DialogContent
          sx={{
            pt: 2.5,
            overflowY: 'auto',
          }}
        >
          {detailLoading && !detail ? (
            <LoadingSkeleton type="Detail" />
          ) : detail ? (
            <Stack spacing={2.5} sx={{ pt: 1, minWidth: 0 }}>
              <Box sx={detailGridSx}>
                <DetailField label={t('setupProfiles:profile')}>
                  <Typography sx={monoSx}>{detail.profile_code || emptyValue}</Typography>
                </DetailField>
                <DetailField label={t('setupProfiles:seedScope')}>
                  <Typography sx={monoSx}>
                    {seedScopeLabel(t, detail.seed_scope) || emptyValue}
                  </Typography>
                </DetailField>
                <DetailField label={t('setupProfiles:seedType')}>
                  <Typography sx={monoSx}>{detail.seed_type || emptyValue}</Typography>
                </DetailField>
                {detail.setup_category && (
                  <DetailField label={t('common:category')}>
                    <StatusBadge
                      status={detail.setup_category}
                      category="generic"
                      apiLabel={categoryLabel(t, detail.setup_category)}
                      size="Sm"
                      tone={CATEGORY_TONE[detail.setup_category] || 'Neutral'}
                    />
                  </DetailField>
                )}
              </Box>

              <Box sx={detailGridSx}>
                <TechList label={t('setupProfiles:requiredTables')} values={detail.required_tables} />
                <TechList label={t('setupProfiles:relatedCapabilities')} values={detail.required_capabilities} />
                <TechList label={t('setupProfiles:readinessChecks')} values={detail.readiness_checks} />
                <TechList label={t('setupProfiles:requiredFields')} values={detail.required_fields} />
                <TechList label={t('setupProfiles:optionalFields')} values={detail.optional_fields} />
              </Box>

              {detail.warnings?.length > 0 && (
                <Alert severity="warning">
                  {detail.warnings.join(' · ')}
                </Alert>
              )}

              {detail.notes && (
                <Typography sx={wrapSx}>{detail.notes}</Typography>
              )}

              {detail.items?.length > 0 && (
                <SectionCard
                  variant="Default"
                  title={t('setupProfiles:profileItems')}
                  noPadding
                  sx={{ mb: 0 }}
                >
                  <DataTable
                    type="Compact"
                    embedded
                    columns={itemColumns}
                    rows={detail.items}
                    getRowKey={(row) => String(row.id)}
                    sx={{
                      overflowX: 'auto',
                      width: '100%',
                      '& table': {
                        width: '100%',
                        minWidth: '100%',
                      },
                    }}
                  />
                </SectionCard>
              )}
            </Stack>
          ) : null}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
